import { takeLatest, put, select, call } from 'redux-saga/effects';
import _ from 'lodash';
import * as actions from '../reportsActions';
import { selectReportsFilters } from '../reportsSelectors';
import { 
  fetchProjects,
  fetchBulkProjectDetails,
  exportBulkComments
} from '../../../services/projects';
import config from '../../../sso/config';
import Cookies from 'js-cookie';

// Formats an ISO timestamp as an en-US calendar date using its UTC components,
// not the browser's local timezone. Timestamps like `closing_start_ts` represent
// a calendar date (e.g. midnight UTC == "that day"), not a specific moment in time.
// Formatting them in local time rolls the displayed date back by one day for any
// browser west of UTC (i.e. every US timezone), silently shifting real closing
// dates shown to users by a day.
const formatUtcDate = (isoDateString) => new Date(isoDateString).toLocaleDateString('en-US', { timeZone: 'UTC' });

// New Saga: Fetch Filtered Projects (Apply Filter)
function* fetchFilteredProjectsSaga(action) {
  try {
    yield put(actions.loading(true));
    
    // Validate that filters exist and provide defaults if missing
    if (!action || !action.filters) {
      const errorMsg = 'No filters provided. Unable to fetch projects.';
      yield put(actions.error(errorMsg));
      yield put(actions.loading(false));
      return;
    }
    
    const filters = action.filters;
    
    // Get user ID for external user filtering
    let userIdParameter = "";
    let userRoleInd = Cookies.get(config.SSO_extUser_Ind);
    if (!userRoleInd || (userRoleInd && userRoleInd === '1')) {
      userIdParameter = localStorage.getItem(config.SSO_user_loginId);
    }
    
    // Check if any status is selected - with null-safe access
    const hasAnyStatusSelected = filters.statusActive || filters.statusClosed || 
                                 filters.statusDropped || filters.statusLocked || 
                                 filters.statusConditionally;
    
    // If no status is selected, show all statuses (set all to true)
    const statusActive = hasAnyStatusSelected ? filters.statusActive : true;
    const statusLocked = hasAnyStatusSelected ? filters.statusLocked : true;
    const statusClosed = hasAnyStatusSelected ? filters.statusClosed : true;
    const statusDropped = hasAnyStatusSelected ? filters.statusDropped : true;
    const statusConditionally = hasAnyStatusSelected ? filters.statusConditionally : true;
    
    // Prepare base filter variables (without pagination)
    const baseFilterVariables = {
      field: 'Modified',
      direction: 'desc',
      StoreNumber: filters.storeNumber,
      SequenceNumber: filters.sequenceNumber,
      City: filters.city,
      State: filters.state,
      ProjectName: filters.projectName,
      Active: statusActive,
      Locked: statusLocked,
      Closed: statusClosed,
      Dropped: statusDropped,
      ConditionallyClosed: statusConditionally,
      DiscretionaryApproval: filters.discretionaryApproval === "true" ? true : 
                              filters.discretionaryApproval === "false" ? false : undefined,
      userId: userIdParameter,
    };
    
    // 1. Fetch paginated data for display
    const paginatedVariables = {
      ...baseFilterVariables,
      page: (filters.page || 0) + 1,
      limit: filters.rowsPerPage || 10,
    };
    const response = yield call(fetchProjects, paginatedVariables);
    
    // Validate response structure
    if (!response || !response.data || !response.data.fetchProjects) {
      throw new Error('Invalid response structure from fetchProjects API');
    }
    
    const projectsData = _.get(response, 'data.fetchProjects.Projects', []);
    const totalCount = _.get(response, 'data.fetchProjects.ProjectsCount', 0);
    const discApprovalCount = _.get(response, 'data.fetchProjects.DiscretionaryApprovalYesCount', 0);
    
    // Ensure projectsData is an array
    if (!Array.isArray(projectsData)) {
      throw new Error('Invalid projects data: expected array');
    }
    
    // Transform data to match expected field format for UI display
    const transformedProjects = projectsData.map((project, index) => {
      try {
        return {
          id: project.closing_id,
          closing_id: project.closing_id,
          projectName: safeTrim(project.closing_name),
          projectNumber: safeTrim(project.project_seq_nbr?.toString() || 'N/A'),
          status: safeTrim(project.status_code_desc || 'N/A'),
          storeNumber: safeTrim(project.store_nbr?.toString() || 'N/A'),
          city: safeTrim(project.city_name || 'N/A'),
          state: safeTrim(project.state_prov_code || 'N/A'),
          createdDate: project.closing_start_ts ? 
            formatUtcDate(project.closing_start_ts) : 'N/A',
          modifiedDate: project.last_change_ts ? 
            formatUtcDate(project.last_change_ts) : 'N/A',
          projectedClosingDate: project.closing_start_ts ? 
            formatUtcDate(project.closing_start_ts) : '',
          realEstateManager: project.remFirstName && project.remLastName ? 
            safeTrim(`${project.remFirstName} ${project.remLastName}`) : '',
          parcels: safeTrim(project.parcel_count),
          discretionaryApproval: project.disc_approval_ind === true || project.disc_approval_ind === 'true' ? 'Yes' : 'No',
        };
      } catch (transformError) {
        console.error(`Error transforming project at index ${index}:`, transformError, project);
        // Return a minimal valid object to prevent complete failure
        return {
          id: project?.closing_id || `error-${index}`,
          closing_id: project?.closing_id || `error-${index}`,
          projectName: 'Error loading project',
          projectNumber: 'N/A',
          status: 'N/A',
          storeNumber: 'N/A',
          city: 'N/A',
          state: 'N/A',
          createdDate: 'N/A',
          modifiedDate: 'N/A',
          projectedClosingDate: '',
          realEstateManager: '',
          parcels: '',
          discretionaryApproval: 'No',
        };
      }
    });
    
    // Store filtered projects (transformed data for display)
    yield put(actions.setFilteredProjects(transformedProjects));
    
    yield put(actions.setTotalCount(totalCount));
    yield put(actions.setDiscApprovalCount(discApprovalCount));
    yield put(actions.loading(false));
  } catch (error) {
    console.error('Error in fetchFilteredProjectsSaga:', error);
    
    // Provide context-aware error messages
    let errorMessage = 'Failed to fetch filtered projects';
    
    if (error.message) {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.networkError) {
      errorMessage = 'Network error: Unable to connect to server';
    }
    
    yield put(actions.error(errorMessage));
    yield put(actions.loading(false));
  }
}

// Helper function to safely trim values
function safeTrim(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

// Helper functions for user mapping
function getUserByRole(users, roleName) {
  if (!Array.isArray(users) || !roleName) return '';
  const user = users.find(u => u?.role_name && u.role_name.trim() === roleName.trim());
  return user ? `${user.extrn_first_name || ''} ${user.extrn_last_name || ''}`.trim() : '';
}

function getUserByRoleIds(users, roleIds) {
  if (!Array.isArray(users) || !Array.isArray(roleIds)) return '';
  for (const roleId of roleIds) {
    const user = users.find(u => u?.role_id == roleId);
    if (user) {
      return `${user.extrn_first_name || ''} ${user.extrn_last_name || ''}`.trim();
    }
  }
  return '';
}

function getUsersByRole(users, roleName) {
  if (!Array.isArray(users) || !roleName) return '';
  const usersWithRole = users.filter(u => u?.role_name && u.role_name.trim() === roleName.trim());
  return usersWithRole.map(u => 
    `${u.extrn_first_name || ''} ${u.extrn_last_name || ''}`.trim()
  ).filter(name => name).join(', ');
}

// New Saga: Generate Bulk Report (Generate Report Button)
function* generateBulkReportSaga(action) {
  try {
    yield put(actions.exportLoading(true));
    
    // Validate action payload
    if (!action) {
      yield put(actions.bulkReportError('Invalid action: no action provided.'));
      yield put(actions.exportLoading(false));
      return;
    }
    
    const { reportType, filters, selectedColumns } = action;
    
    // Validate reportType
    if (!reportType) {
      yield put(actions.bulkReportError('No report type specified.'));
      yield put(actions.exportLoading(false));
      return;
    }
    
    // Validate filters
    if (!filters) {
      yield put(actions.bulkReportError('No filters provided. Please apply filters first.'));
      yield put(actions.exportLoading(false));
      return;
    }
    
    // Get user ID for external user filtering
    let userIdParameter = "";
    let userRoleInd = Cookies.get(config.SSO_extUser_Ind);
    if (!userRoleInd || (userRoleInd && userRoleInd === '1')) {
      userIdParameter = localStorage.getItem(config.SSO_user_loginId);
    }
    
    // Check if any status is selected
    const hasAnyStatusSelected = filters.statusActive || filters.statusClosed || 
                                 filters.statusDropped || filters.statusLocked || 
                                 filters.statusConditionally;
    
    // If no status is selected, show all statuses (set all to true)
    const statusActive = hasAnyStatusSelected ? filters.statusActive : true;
    const statusLocked = hasAnyStatusSelected ? filters.statusLocked : true;
    const statusClosed = hasAnyStatusSelected ? filters.statusClosed : true;
    const statusDropped = hasAnyStatusSelected ? filters.statusDropped : true;
    const statusConditionally = hasAnyStatusSelected ? filters.statusConditionally : true;
    
    // Prepare filter variables for bulk API
    const bulkFilterVariables = {
      field: 'Modified',
      direction: 'desc',
      StoreNumber: filters.storeNumber,
      SequenceNumber: filters.sequenceNumber,
      City: filters.city,
      State: filters.state,
      ProjectName: filters.projectName,
      Active: statusActive,
      Locked: statusLocked,
      Closed: statusClosed,
      Dropped: statusDropped,
      ConditionallyClosed: statusConditionally,
      DiscretionaryApproval: filters.discretionaryApproval === "true" ? true : 
                              filters.discretionaryApproval === "false" ? false : undefined,
      userId: userIdParameter,
    };
    
    let reportData = [];
    
    switch (reportType) {
      case 'projectDetails': {
        // Call bulk project details API with filters - request Excel format
        const bulkResponse = yield call(fetchBulkProjectDetails, {
          ...bulkFilterVariables,
          includeProjectDetails: !!selectedColumns?.projectDetails,
          includeStoreDetails: !!selectedColumns?.storeDetails,
          includeUserDetails: !!selectedColumns?.userDetails,
          downloadExcel: true, // Request Excel file from backend
        });
        
        const bulkData = _.get(bulkResponse, 'data.fetchBulkProjectDetails', {});
        
        if (!bulkData.Success) {
          yield put(actions.bulkReportError(bulkData.Message || 'Failed to fetch project details'));
          yield put(actions.exportLoading(false));
          return;
        }
        
        // Check if Excel data is returned
        if (bulkData.ExcelData) {
          // Backend returned Excel file as base64 - store formatted data for download
          reportData = {
            type: 'formatted',
            content: bulkData.ExcelData,
            fileName: `Project_Details_${new Date().toISOString().split('T')[0]}.xlsx`
          };
          break;
        }
        
        // Fallback: process Projects array if Excel generation failed
        const enrichedProjects = _.get(bulkData, 'Projects', []);
        
        // Transform data for report display
        reportData = enrichedProjects.map(project => {
          const transformed = {
            id: project.closing_id,
            projectName: safeTrim(project.closing_name),
            projectNumber: safeTrim(project.project_seq_nbr?.toString() || 'N/A'),
            status: safeTrim(project.status_code_desc || 'N/A'),
            storeNumber: safeTrim(project.store_nbr?.toString() || 'N/A'),
            city: safeTrim(project.city_name || 'N/A'),
            state: safeTrim(project.state_prov_code || 'N/A'),
            createdDate: project.closing_start_ts ? 
              formatUtcDate(project.closing_start_ts) : 'N/A',
            modifiedDate: project.last_change_ts ? 
              formatUtcDate(project.last_change_ts) : 'N/A',
            projectedClosingDate: project.closing_start_ts ? 
              formatUtcDate(project.closing_start_ts) : '',
            parcels: safeTrim(project.parcel_count),
            discretionaryApproval: project.disc_approval_ind ? 'Yes' : 'No',
          };
          
          // Add project details fields if included
          if (selectedColumns?.projectDetails && project.projectDetails) {
            // Override projectedClosingDate if projectDetails has closing_ts
            if (project.projectDetails.closing_ts) {
              transformed.projectedClosingDate = formatUtcDate(project.projectDetails.closing_ts);
            }
            transformed.sellers = safeTrim(project.projectDetails.seller_count);
            transformed.companyNumber = safeTrim(project.projectDetails.jde_company_nbr);
            transformed.projectType = safeTrim(project.projectDetails.project_type_desc);
            transformed.closingType = safeTrim(project.projectDetails.closing_type_desc);
            transformed.address = safeTrim(project.projectDetails.addr_line1);
          }
          
          // Add store details fields if included
          if (selectedColumns?.storeDetails && project.storeDetails) {
            transformed.storeSequenceNumber = safeTrim(project.storeDetails.store_seq_nbr);
            transformed.address = safeTrim(project.storeDetails.address_line_1 || transformed.address);
            transformed.storeType = safeTrim(project.storeDetails.store_type_code);
            transformed.projectType = safeTrim(transformed.projectType || project.storeDetails.project_type_code);
            transformed.businessUnitType = safeTrim(project.storeDetails.bu_type_code);
            transformed.companyNumber = safeTrim(transformed.companyNumber || project.storeDetails.company);
            transformed.darkStoreNumber = safeTrim(project.storeDetails.dark_store_nbr);
          }
          
          // Add user details fields if included
          if (selectedColumns?.userDetails && project.users && project.users.length > 0) {
            transformed.attorney = getUserByRole(project.users, 'GENERAL_COUNCIL_ROLE');
            transformed.paralegal = getUserByRoleIds(project.users, [12, 5]);
            transformed.realEstateManager = getUserByRole(project.users, 'REAL_ESTATE_MANAGER_ROLE');
            transformed.designManager = getUserByRole(project.users, 'DISTRICT_MANAGER_ROLE');
            transformed.auditor = getUserByRoleIds(project.users, [4, 13]);
            transformed.regionalVP = getUserByRole(project.users, 'REGIONAL_VICE_PRESIDENT_ROLE');
            transformed.accountant = getUserByRole(project.users, 'ACCOUNTANT_ROLE');
            transformed.outsideCounsel = getUsersByRole(project.users, 'OUTSIDE_COUNCIL_ROLE');
            transformed.cecFirm = getUserByRole(project.users, 'CIVIL_ENGINEER_ROLE');
            
            // Set assignedUsers for display
            transformed.assignedUsers = project.users
              .filter(u => u.extrn_first_name && u.extrn_last_name)
              .map(u => safeTrim(`${u.extrn_first_name} ${u.extrn_last_name}`));
          }
          
          return transformed;
        });
        break;
      }
      
      case 'commentsAnalysis': {
        // Call bulk export comments API with filters - backend returns formatted Excel data
        const exportResponse = yield call(exportBulkComments, bulkFilterVariables);
        
        // Backend returns formatted HTML/Excel content directly
        const formattedData = _.get(exportResponse, 'data.exportBulkProjectComments', null);
        
        if (!formattedData) {
          yield put(actions.bulkReportError('Failed to export comments'));
          yield put(actions.exportLoading(false));
          return;
        }
        
        // Store the formatted data - CommentsTab will handle download directly
        reportData = { 
          type: 'formatted', 
          content: formattedData,
          fileName: `Comments_Report_${new Date().toISOString().split('T')[0]}.xlsx`
        };
        break;
      }
      
      default:
        yield put(actions.bulkReportError('Invalid report type'));
        yield put(actions.exportLoading(false));
        return;
    }
    
    yield put(actions.setData(reportData));
    yield put(actions.bulkReportSuccess(reportType, selectedColumns));
    yield put(actions.exportLoading(false));
  } catch (error) {
    console.error('Error in generateBulkReportSaga:', error);
    
    // Provide context-aware error messages
    let errorMessage = 'Failed to generate report';
    
    if (error.message) {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.networkError) {
      errorMessage = 'Network error: Unable to connect to server';
    }
    
    yield put(actions.bulkReportError(errorMessage));
    yield put(actions.exportLoading(false));
  }
}

export default function* reportsSaga() {
  yield takeLatest(actions.FETCH_FILTERED_PROJECTS, fetchFilteredProjectsSaga);
  yield takeLatest(actions.GENERATE_BULK_REPORT, generateBulkReportSaga);
}

// Export for testing
export { fetchFilteredProjectsSaga, generateBulkReportSaga };
